import { useState, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/shared/ui/dropdown-menu";
import type { CategoryAttributeResponse } from "@/services/api";
import type { SelectedAttribute } from "../hooks/useCategoryFormData";

interface AttributeSelectorProps {
  availableAttributes: CategoryAttributeResponse[];
  selectedAttributes: SelectedAttribute[];
  onAdd: (attributeId: number) => void;
  onRemove: (attributeId: number) => void;
  onUpdate: (attributeId: number, isMandatory: boolean) => void;
  isLoading: boolean;
}

export function AttributeSelector({
  availableAttributes,
  selectedAttributes,
  onAdd,
  onRemove,
  onUpdate,
  isLoading,
}: AttributeSelectorProps) {
  const [open, setOpen] = useState(false);

  const selectedIds = useMemo(
    () => new Set(selectedAttributes.map((a) => a.attributeId)),
    [selectedAttributes]
  );
  const unselectedAttributes = useMemo(
    () => availableAttributes.filter((attr) => !selectedIds.has(attr.id)),
    [availableAttributes, selectedIds]
  );
  const selectedWithData = useMemo(
    () =>
      selectedAttributes
        .map((selected) => {
          const attr = availableAttributes.find(
            (a) => a.id === selected.attributeId
          );
          return attr ? { ...selected, attribute: attr } : null;
        })
        .filter(
          (
            item
          ): item is SelectedAttribute & {
            attribute: CategoryAttributeResponse;
          } => item !== null
        ),
    [selectedAttributes, availableAttributes]
  );

  if (availableAttributes.length === 0) {
    return (
      <p className="text-sm text-ink-500 text-center py-4 border border-grid-200 rounded-lg">
        No attributes available. Create attributes first.
      </p>
    );
  }

  const handleSelect = (attributeId: number) => {
    onAdd(attributeId);
    setOpen(false);
  };

  const placeholder =
    unselectedAttributes.length === 0
      ? "All attributes selected"
      : "Select an attribute...";

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="w-full justify-between h-10"
            disabled={isLoading || unselectedAttributes.length === 0}
          >
            <span className="text-sm text-ink-400 text-left flex-1">
              {placeholder}
            </span>
            <div className="shrink-0">
              <ChevronDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-80 max-h-[300px] overflow-y-auto"
          align="start"
          side="bottom"
        >
          {unselectedAttributes.length === 0 ? (
            <div className="px-2 py-6 text-center text-sm text-ink-500">
              All attributes selected
            </div>
          ) : (
            unselectedAttributes.map((attr) => (
              <DropdownMenuItem
                key={attr.id}
                onClick={() => handleSelect(attr.id)}
                className="cursor-pointer"
              >
                {attr.name}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedWithData.length > 0 && (
        <div className="space-y-2 border border-grid-200 rounded-lg p-3 max-h-[300px] overflow-y-auto">
          {selectedWithData.map((item) => (
            <div
              key={item.attributeId}
              className="border border-grid-200 rounded-lg p-3 space-y-2"
            >
              <div className="font-medium text-ink-900">
                {item.attribute.name}
              </div>
              {item.attribute.values.length > 0 && (
                <div className="text-xs text-ink-500">
                  Values: {[...item.attribute.values]
                    .sort((a, b) => a.order - b.order)
                    .map((v) => v.value)
                    .join(", ")}
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-grid-200">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`mandatory-${item.attributeId}`}
                    checked={item.isMandatory}
                    onCheckedChange={(checked) =>
                      onUpdate(item.attributeId, checked === true)
                    }
                    disabled={isLoading}
                  />
                  <label
                    htmlFor={`mandatory-${item.attributeId}`}
                    className="text-sm text-ink-700 cursor-pointer leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Mandatory
                  </label>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(item.attributeId)}
                  disabled={isLoading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {selectedAttributes.length > 0 && (
        <p className="text-xs text-ink-500">
          {selectedAttributes.length} attribute(s) selected
        </p>
      )}
    </>
  );
}
